import { useForm } from '@tanstack/react-form';
import { useAtom, useSetAtom } from 'jotai';
import { uniqueId } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';
import type { AreaSelection, DevelopmentPaceScenarioFormData } from './types';
import { saveScenarioLoadableAtom, toastAtom } from './atoms';
import {
  buildCreatePayload,
  buildUpdatePayload,
  DEFAULT_AREA_SELECTION,
  GLOBAL_SELECTION_ID,
  findInvalidAreaIds,
  isAreaSelectionComplete,
  pickSeedSource,
  seedAreaFromSource,
  type FormMode,
} from './utils';

// ─────────────────────────────────────
// Context type
// ─────────────────────────────────────

interface ScenarioFormContextType {
  form: ReturnType<typeof useCreateForm>;
  mode: FormMode;
  selectedId: string | number;
  setSelectedId: (id: string | number) => void;
  addArea: () => void;
  removeArea: (id: number) => void;
  markAreaConfigured: (areaId: number) => void;
  handleSave: () => void;
  isSaving: boolean;
}

const ScenarioFormContext = createContext<ScenarioFormContextType | null>(null);

// ─────────────────────────────────────
// Provider
// ─────────────────────────────────────

interface ScenarioFormProviderProps {
  initialValues: DevelopmentPaceScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  geometries: Record<string, GeoJSON.Geometry>;
  children: ReactNode;
}

const useCreateForm = (
  defaultValues: DevelopmentPaceScenarioFormData,
  geometries: Record<string, GeoJSON.Geometry>
) => {
  return useForm({
    defaultValues,
    validators: {
      onChange: ({ value }: { value: DevelopmentPaceScenarioFormData }) => {
        const hasIncompleteArea = value.areas.some(
          (a) => !isAreaSelectionComplete(a)
        );
        if (hasIncompleteArea) return 'All areas must have a selection';

        // Gate at the form level so an out-of-range value blocks save even while
        // its panel is unmounted — e.g. after switching the pace model from the
        // Global panel makes a previously-valid per-area value invalid.
        if (findInvalidAreaIds(value).size > 0) {
          return 'One or more area parameters are out of range';
        }

        const ids = getMemoizedOverlappingAreaIds(value.areas, geometries);
        return ids.size > 0
          ? 'One or more areas have overlapping geometries'
          : undefined;
      },
    },
  });
};

/**
 * Provides form state management for the development pace scenario form.
 * Wraps @tanstack/react-form. Field-level validators are on each form.Field;
 * form-level onChange validator checks geometry overlap via the shared,
 * memoized getMemoizedOverlappingAreaIds.
 * @param {ScenarioFormProviderProps} props - The provider props
 * @return {JSX.Element} The provider component
 */
export function ScenarioFormProvider({
  initialValues,
  mode,
  scenarioId,
  geometries,
  children,
}: ScenarioFormProviderProps): JSX.Element {
  const form = useCreateForm(initialValues, geometries);
  const [selectedId, setSelectedId] = useState<string | number>(
    GLOBAL_SELECTION_ID
  );
  const [saveState, triggerSave] = useAtom(saveScenarioLoadableAtom);
  const removedAreaIdsRef = useRef<number[]>([]);
  // The last area whose pace params were edited this session — the seed source
  // for the next Add Area (spec 5.3). Falls back to list order when unset or when
  // that area has since been deleted (pickSeedSource handles the miss).
  const lastConfiguredAreaIdRef = useRef<number | null>(null);
  const history = useHistory();

  const isSaving = saveState.state === 'loading';
  const setToast = useSetAtom(toastAtom);

  /**
   * Adds a new area. It seeds its pace params from the most-recently-configured
   * area (a plain copy, no linkage); the very first area of a scenario opens with
   * the pinned defaults. Geometry, alias, and notes are never copied.
   */
  const addArea = useCallback(() => {
    const id = Number(uniqueId());
    const currentAreas = form.getFieldValue('areas');
    const source = pickSeedSource(
      currentAreas,
      lastConfiguredAreaIdRef.current
    );
    const newArea = source
      ? seedAreaFromSource(source, id)
      : { ...DEFAULT_AREA_SELECTION, id };
    form.setFieldValue('areas', [...currentAreas, newArea]);
    setSelectedId(id);
  }, [form]);

  /**
   * Records an area as the most-recently-configured, so the next Add Area seeds
   * from it. Called when a pace, lifecycle, or upside field changes.
   * @param {number} areaId - The area whose params were edited
   */
  const markAreaConfigured = useCallback((areaId: number) => {
    lastConfiguredAreaIdRef.current = areaId;
  }, []);

  /**
   * Removes an area by ID. Tracks backend IDs for _destroy on update.
   * @param {number} id - The area ID to remove
   */
  const removeArea = useCallback(
    (id: number) => {
      const currentAreas = form.getFieldValue('areas');
      const area = currentAreas.find((a: AreaSelection) => a.id === id);
      if (area?.status === 'saved') {
        removedAreaIdsRef.current.push(area.id);
      }
      form.setFieldValue(
        'areas',
        currentAreas.filter((a: AreaSelection) => a.id !== id)
      );

      if (selectedId === id) {
        setSelectedId(GLOBAL_SELECTION_ID);
      }
    },
    [form, selectedId]
  );

  /**
   * Saves the scenario to the backend.
   */
  const handleSave = useCallback(async () => {
    const values = form.state.values;
    setToast(null);

    if (mode === 'create') {
      const payload = buildCreatePayload(values);
      try {
        const response = await triggerSave({
          mode: 'create',
          payload,
        });
        setToast({ message: 'Configuration saved', type: 'success' });
        history.replace(
          routes.developmentPaceScenarioDetailPath.replace(
            ':id',
            String(response.id)
          )
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    } else {
      const payload = buildUpdatePayload(
        values,
        removedAreaIdsRef.current,
        form.state.fieldMeta
      );
      if (!payload) return;
      try {
        await triggerSave({
          mode: 'edit',
          id: Number(scenarioId),
          payload,
        });
        setToast({ message: 'Configuration saved', type: 'success' });
        window.location.reload();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    }
  }, [form, mode, scenarioId, triggerSave, history, setToast]);

  const value = useMemo(
    () => ({
      form,
      mode,
      selectedId,
      setSelectedId,
      addArea,
      removeArea,
      markAreaConfigured,
      handleSave,
      isSaving,
    }),
    [
      form,
      mode,
      selectedId,
      addArea,
      removeArea,
      markAreaConfigured,
      handleSave,
      isSaving,
    ]
  );

  return (
    <ScenarioFormContext.Provider value={value}>
      {children}
    </ScenarioFormContext.Provider>
  );
}

// ─────────────────────────────────────
// Hook
// ─────────────────────────────────────

/**
 * Returns the scenario form context. Must be used within a ScenarioFormProvider.
 * @return {ScenarioFormContextType} The scenario form context
 * @throws {Error} If used outside of ScenarioFormProvider
 */
export function useScenarioForm(): ScenarioFormContextType {
  const context = useContext(ScenarioFormContext);
  if (context === null) {
    throw new Error(
      'useScenarioForm must be used within a ScenarioFormProvider'
    );
  }
  return context;
}
